import { store } from "../../redux/store";
import { posthog } from "../../lib/posthog";

type EventProperties = Record<string, unknown>;

export const ANALYTICS_EVENTS = {
  EMAIL_ADDED: "email_added",
  CONSUMER_SIGN_IN: "consumer_sign_in",
  PRODUCT_VERIFIED: "product_verified",
  REVIEW_SUBMITTED: "review_submitted",
  PRODUCT_COLLECTED: "product_collected",
  HELP_CENTER_OPENED: "help_center_opened",
  PRODUCT_REDEEM_FAILED: "product_redeem_failed",
  CONSUMER_SIGN_IN_FAILED: "consumer_sign_in_failed",
  REVIEW_SUBMISSION_FAILED: "review_submission_failed",
  COLLECTED_PRODUCT_VIEWED: "collected_product_viewed",
  ACCOUNT_SETTINGS_UPDATED: "account_settings_updated",
  BATCH_LANDING_PAGE_VIEWED: "batch_landing_page_viewed",
  BRAND_PROFILE_PAGE_VIEWED: "brand_profile_page_viewed",
  RETURNED_FROM_HELP_CENTER: "returned_from_help_center",
  EDUCATIONAL_CONTENT_VIEWED: "educational_content_viewed",
} as const;

const sanitizeProperties = (properties: EventProperties) => {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined),
  );
};

export const captureEvent = (
  eventName: string,
  properties: EventProperties = {},
) => {
  posthog.capture(eventName, sanitizeProperties(properties));
};

export const trackConsumerEvent = (
  eventName: (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS],
  properties: EventProperties = {},
) => {
  captureEvent(eventName, {
    ...properties,
    timestamp: properties.timestamp || new Date().toISOString(),
  });
};

export const identifyAnalyticsUser = (
  userId: string,
  userProperties: EventProperties = {},
) => {
  if (!userId) return;

  try {
    const currentDistinctId = posthog.get_distinct_id();
    if (currentDistinctId && currentDistinctId !== userId) {
      const isNewPhone = /^\+?\d+$/.test(userId);
      const isCurrentEmail = currentDistinctId.includes("@");
      if (isNewPhone && isCurrentEmail) {
        posthog.reset();
      }
    }
  } catch (e) {
    console.error("Error checking posthog distinct ID:", e);
  }

  const updatedProperties = { ...userProperties };
  const isPhone = /^\+?\d+$/.test(userId);
  if (isPhone) {
    if (userProperties.email && userProperties.email !== userId) {
      updatedProperties.userEmail = userProperties.email;
    }
    updatedProperties.email = userId;
  }

  posthog.identify(userId, sanitizeProperties(updatedProperties));
};

export const resetAnalyticsUser = () => {
  posthog.reset();
};

/**
 * Prefer profile `userData._id`, else `userData.email` as `user_email`.
 * Omits both when profile has no usable identity (empty initial state / not loaded).
 */
export const getAnalyticsUserIdentity = (): {
  user_phone?: string;
  user_email?: string;
  user_id?: string;
} => {
  const userData = store.getState().profileSlice?.userData;
  if (!userData) return {};

  const rawPhone = userData.mobileNumber || "";
  const cleanPhone = rawPhone.replace(/^\+1/, "").trim();

  const identity: {
    user_phone?: string;
    user_email?: string;
    user_id?: string;
  } = {};

  if (cleanPhone) {
    identity.user_phone = cleanPhone;
  }
  if (userData.email?.trim()) {
    identity.user_email = userData.email.trim();
  }
  if (userData._id?.trim()) {
    identity.user_id = userData._id.trim();
  }

  return identity;
};
